/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import { extraAutoresponderConfigTemplate, getClientTimezone, getSyncableOptions, isDateInPast } from '../utils';
import { checkTrackedPremiumFieldsErrorForForm, createApiThunk } from '../../../shared/utils';
import { formAmendForm, ControlType, doCreateIdentifier } from '../../../shared/form-lib';
import win from '../../../shared/win';

const LOCATION = 'SETTINGS REDUCER';

export const defaultState = {
  isSavingOption: false,

  ui: {
    activeAutoresponderEmailConfigId: null,
    activeNotificationEmailConfig: null,
    activeAutoresponderId: 'default',
    activeNotificationSection: '',
    activeSidebarTab: 'general',
    activePaymentSection: '',
    activeCustomTextIndex: 0,
    showMobileSidebar: false,
    transactionsMaxRow: 50,
  },

  form: {
    isTrackedPremiumFieldsErrorForm: false,
    disableSubmitOptionUseText: '',
    disableSubmitOptionUse: false,
    unallowedPremiumFields: [],
    hasInventoryField: false,
    internalIdentifiers: {},
    displayIdentifiers: {},
    hasSubmitAction: false,
    hasQuizTimer: false,
    isJsonEdited: false,
    isFetching: true,
    statusText: '',
    formData: {},
    status: '',
    error: '',
    url: '',
    id: '',
  },

  trackedNotificationValues: {
    autoresponderAttachmentFormat: 'docx',
    notificationAttachmentFormat: 'docx',
    notificationType: 'submission',
    isUpdatingEmails: false,
    notificationMonthDay: 1,
    notificationWeekDay: 0,
  },

  notificationEmailConfig: {
    isFetching: true,
    values: {},
    error: '',
  },

  extraAutoresponders: {
    isFetching: true,
    values: [],
    error: '',
  },

  userOptions: {
    notificationEmails: [],
    isFetching: true,
    error: '',

    credentials: {
      isFetching: true,
      error: '',
    },
  },

  allSheets: {
    isFetching: true,
    sheets: [],
    error: '',
  },

  formSheets: {
    retrieved: false,
    isFetching: true,
    selected: [],
    titles: [],
    error: '',
  },

  formTransfer: {
    copyCloudStorageData: false,
    isTransferring: false,
    impossibleReason: '',
    isTransferred: false,
    copyResults: false,
    isPossible: false,
    newOwnerEmail: '',
  },

  formCustomTexts: {
    isFetching: true,
    texts: {},
    error: '',
  },

  accountQuota: {
    isFetching: true,
    error: '',
    emails: {
      percentage: 0,
      used: 0,
    },
    geolocation: {
      remaining: 0,
      limit: 0,
      used: 0,
    },
  },

  customDocuments: {
    autorespTemplate: 'default',
    isUploadingTemplate: false,
    notifTemplate: 'default',
    templateUploadError: '',
    selectedTemplate: null,
    isFetching: false,
    processKeys: {},
    templates: [],
    columns: [],
    error: '',
    templateUploadProgress: {
      humanSize: 0,
      loaded: 0,
    },
  },

  formPaymentHistory: {
    isRefreshing: false,
    isFetching: false,
    transactions: [],
    prevMore: false,
    nextMore: false,
    nextCursor: '',
    prevCursor: '',
    columns: [],
    error: '',
    filters: {
      timestamp: { startTimestamp: '', endTimestamp: '' },
      transactionId: '',
      currencies: [],
      gateways: [],
      statuses: [],
      amount: '',
      reason: '',
    },
  },

  storageServicesAuthorization: {
    isGoogleDriveAuthorized: false,
    isOneDriveTokenGranted: false,
    isDropboxTokenGranted: false,
    activeAuthType: '',
    activeAuthUrl: '',
  },

  pendingStorageServiceInfo: {},
};

export const fetchForm = createApiThunk({
  messages: { notFound: 'Requested form could not be found.' },
  url: (formId) => `/api/v2/user/form/${formId}`,
  typePrefix: 'settings/fetchForm',
  action: 'fetch form',
  location: LOCATION,
});

export const fetchUserOptions = createApiThunk({
  typePrefix: 'settings/fetchUserOptions',
  action: 'fetch user options',
  url: '/api/v2/user/options',
  location: LOCATION,
});

export const fetchAllSheetsTitles = createApiThunk({
  typePrefix: 'settings/fetchAllSheetsTitles',
  action: 'fetch all user forms sheets title',
  url: '/api/v2/user/forms/all_sheets',
  location: LOCATION,
});

export const fetchFormSheetTitles = createApiThunk({
  url: (formId) => `/api/v2/user/forms/${formId}/sheet_titles`,
  typePrefix: 'settings/fetchFormSheetTitles',
  action: 'fetch form sheet titles',
  location: LOCATION,
});

export const fetchUserCredentials = createApiThunk({
  typePrefix: 'settings/fetchUserCredentials',
  action: 'fetch user credentials',
  url: '/api/v2/user/credentials',
  location: LOCATION,
});

export const fetchAccountQuota = createApiThunk({
  typePrefix: 'settings/fetchAccountQuota',
  action: 'fetch account quota',
  url: '/api/v2/user/quota',
  location: LOCATION,
});

export const fetchFormCustomTexts = createApiThunk({
  url: (formId) => `/api/v2/user/form_custom_texts/${formId}`,
  typePrefix: 'settings/fetchFormCustomTexts',
  action: 'fetch form custom texts',
  location: LOCATION,
});

export const fetchFormPaymentHistory = createApiThunk({
  url: ({ formId, cursor = '', direction = 'forward', pageSize = 50 }) =>
    `/api/v2/user/payment_log/${formId}?cursor_str=${cursor}&direction=${direction}&page_size=${pageSize}`,
  typePrefix: 'settings/fetchFormPaymentHistory',
  action: 'fetch form payments',
  location: LOCATION,
});

export const fetchCustomDocTemplates = createApiThunk({
  url: (formId) => `/api/v2/user/${formId}/custom_templates`,
  action: 'fetch form custom documents templates',
  typePrefix: 'settings/fetchCustomDocTemplates',
  location: LOCATION,
});

export const fetchNotificationEmailConfig = createApiThunk({
  url: (formId) => `/api/v2/user/notification_emails/${formId}`,
  typePrefix: 'settings/fetchNotificationEmailConfig',
  action: 'fetch notification email config',
  location: LOCATION,
});

export const fetchExtraAutoresponders = createApiThunk({
  url: (formId) => `/api/v2/user/${formId}/extra_autoresponders`,
  typePrefix: 'settings/fetchExtraAutoresponders',
  action: 'fetch extra autoresponders',
  location: LOCATION,
});

const settingsSlice = createSlice({
  initialState: defaultState,
  name: 'settings',
  reducers: {
    setSavingOptions: (state, { payload }) => {
      state.isSavingOption = payload;
    },
    setUIStatus: (state, { payload }) => {
      state.ui = { ...state.ui, ...payload };
    },
    setStorageServicesStatus: (state, { payload }) => {
      state.storageServicesAuthorization = { ...state.storageServicesAuthorization, ...payload };
    },
    setPendingStorageServiceInfo: (state, { payload }) => {
      state.pendingStorageServiceInfo = { ...payload };
    },
    setFormJsonEdited: (state, { payload }) => {
      state.form.isJsonEdited = payload;
    },
    setFormProps: (state, { payload }) => {
      const { isFormData, values } = payload;

      if (isFormData) {
        state.form.formData = { ...state.form.formData, ...values };
      } else {
        state.form = { ...state.form, ...values };
      }
    },
    setUserOptionsProps: (state, { payload }) => {
      const { values, isCred } = payload;

      if (isCred) {
        state.userOptions.credentials = { ...state.userOptions.credentials, ...values };
      } else {
        state.userOptions = { ...state.userOptions, ...values };
      }
    },
    setFormCustomTexts: (state, { payload }) => {
      state.formCustomTexts.texts = { ...state.formCustomTexts.texts, ...payload };
    },
    setFormTransferProps: (state, { payload }) => {
      state.formTransfer = { ...state.formTransfer, ...payload };
    },
    setFormSheetsProps: (state, { payload }) => {
      state.formSheets = { ...state.formSheets, ...payload };
    },
    setCustomDocumentUploadProps: (state, { payload }) => {
      state.customDocuments = { ...state.customDocuments, ...payload };
    },
    updateCustomDocumentTemplates: (state, { payload }) => {
      const { values: { deletedTemplateKey = null, newTemplate = null } = {} } = payload;

      if (deletedTemplateKey) {
        state.customDocuments.templates = state.customDocuments.templates?.filter(
          (template) => template.template_key !== deletedTemplateKey
        );
      }

      if (newTemplate) {
        state.customDocuments.templates = [newTemplate, ...(state.customDocuments.templates || [])];
      }
    },
    updateCustomDocumentsProcessKeys: (state, { payload }) => {
      const {
        values: { type, deletedTemplateKey },
      } = payload;

      if (type === 'add') {
        state.customDocuments.processKeys[deletedTemplateKey] = true;
        state.customDocuments.selectedTemplate = null;
      } else if (type === 'delete') {
        delete state.customDocuments.processKeys[deletedTemplateKey];
      }
    },
    setTrackedNotificationValues: (state, { payload }) => {
      state.trackedNotificationValues = { ...state.trackedNotificationValues, ...payload };
    },
    updateNotificationEmailConfig: (state, { payload }) => {
      state.notificationEmailConfig.values = { ...payload };
    },
    setIsRefreshingPaymentHistory: (state) => {
      state.formPaymentHistory.isRefreshing = true;
    },
    updateFormPaymentHistory: (state, { payload }) => {
      state.formPaymentHistory.filters = { ...state.formPaymentHistory.filters, ...payload };
    },
    updateExtraAutoresponder: (state, { payload }) => {
      const { autoresponder, newId } = payload;

      const newExtraAutoresponders = state.extraAutoresponders.values.map((autoresp) => {
        if (autoresp.id === autoresponder.id) {
          if (newId) return { ...autoresponder, id: newId };
          else return autoresponder;
        }
        return autoresp;
      });

      state.extraAutoresponders.values = newExtraAutoresponders;
      state.ui.activeAutoresponderId = newId ? newId : autoresponder.id;
    },
    createExtraAutoresponder: (state) => {
      const { values: oldExtraAutoresponders } = state.extraAutoresponders;
      const placeholderId = doCreateIdentifier(oldExtraAutoresponders);
      const newAutoresponder = { ...extraAutoresponderConfigTemplate, id: placeholderId };
      state.extraAutoresponders.values = [...oldExtraAutoresponders, newAutoresponder];
      state.ui.activeAutoresponderId = placeholderId;
    },
    deleteExtraAutoresponder: (state, { payload }) => {
      const { values: oldExtraAutoresponders } = state.extraAutoresponders;
      const { autoresponderId } = payload;
      const newExtraAutoresponders = oldExtraAutoresponders.filter((autoresp) => autoresp.id !== autoresponderId);
      state.extraAutoresponders.values = newExtraAutoresponders;
    },
  },
  extraReducers: (settings) => {
    // FORM
    settings
      .addCase(fetchForm.pending, (state) => {
        state.form.isFetching = true;
      })
      .addCase(fetchForm.fulfilled, (state, { payload }) => {
        state.form.isFetching = false;

        if (!payload.definition || !payload.definition.pages) {
          state.form.error = 'Form has no definition/pages';
          return;
        }

        const form = formAmendForm(payload);
        state.form.url = `${location.origin}/form/${form.id}`;
        state.form.formData = form;
        state.form.id = form.id;

        if (!form.storage_service) {
          state.form.formData.storage_service = 'google_drive'; // DO SOMETHNG ABOUT THIS
        }

        if (!form.options) {
          state.form.formData.options = {};
        }

        const { disableSubmitOptionUseText, disableSubmitOptionUse, logicHasSubmitAction, hasQuizTimer } =
          getSyncableOptions(form);

        state.form.disableSubmitOptionUseText = disableSubmitOptionUseText;
        state.form.disableSubmitOptionUse = disableSubmitOptionUse;
        state.form.logicHasSubmitAction = logicHasSubmitAction;
        state.form.hasQuizTimer = hasQuizTimer;

        if (form.unallowed_premium_fields?.length > 0) {
          state.form.isTrackedPremiumFieldsErrorForm = checkTrackedPremiumFieldsErrorForForm(form.id);
          state.form.unallowedPremiumFields = form.unallowed_premium_fields;
        }

        state.form.formData.notification_time = form.notification_time || '06:00:00';

        if (form.notification_value) {
          if (form.notification_type === 'weekly') {
            state.trackedNotificationValues.notificationWeekDay = form.notification_value;
          } else if (form.notification_type === 'monthly') {
            state.trackedNotificationValues.notificationMonthDay = form.notification_value;
          } else {
            state.trackedNotificationValues.notificationWeekDay =
              form.notification_value > 6 ? 0 : form.notification_value; // Valid week day (0 to 6)
            state.trackedNotificationValues.notificationMonthDay =
              form.notification_value > 0 ? form.notification_value : 1; // Valid month day (1 to 31)
          }
        }

        if (['pdf', 'docx'].includes(form.notification_attachment_format)) {
          state.trackedNotificationValues.notificationAttachmentFormat = form.notification_attachment_format;
        }

        if (['pdf', 'docx'].includes(form.options.autoresponder_attachment_format)) {
          state.trackedNotificationValues.autoresponderAttachmentFormat = form.options.autoresponder_attachment_format;
        }

        if (form.notification_type !== 'off') {
          state.trackedNotificationValues.notificationType = form.notification_type;
        }

        // Since enable_submission_start_date was introduced after `submission_start_date`,
        // Setting enable_submission_start_date for the UI based on the availability of `submission_start_date`
        state.form.formData.options.enable_submission_start_date = form.submission_start_date ? true : false;

        // Since form_closure_type was introduced after `deadline` and `max_submissions`,
        // Setting form_closure_type for the UI based on the availability of `deadline` and `max_submissions`:
        // - If both deadline and max_submissions are set, form_closure_type is 'deadline_with_max_submissions'.
        // - If only deadline is set, form_closure_type is 'deadline'.
        // - If only max_submissions is set, form_closure_type is 'max_submissions'.
        // - If neither is set, form_closure_type defaults to 'always_open'.
        if (form.deadline && form.max_submissions) {
          state.form.formData.options.form_closure_type = 'deadline_with_max_submissions';
        }

        if (form.deadline && !form.max_submissions) {
          if (isDateInPast(form.deadline)) {
            state.form.formData.options.form_closure_type = 'closed';
          } else {
            state.form.formData.options.form_closure_type = 'deadline';
          }
        }

        if (!form.deadline && form.max_submissions) {
          state.form.formData.options.form_closure_type = 'max_submissions';
        }

        if (!form.deadline && !form.max_submissions) {
          state.form.formData.options.form_closure_type = 'always_open';
        }

        state.form.hasInventoryField = form.definition.pages.some(({ controls }) =>
          controls.some(({ type }) => type === ControlType.INVENTORY)
        );
      })
      .addCase(fetchForm.rejected, (state, { payload }) => {
        const { uiErrorMessage, statusText, status, error } = payload;

        const newStatusText = error?.includes('form is inactive') ? 'FORBIDDEN' : statusText;
        state.form.statusText = newStatusText;
        state.form.error = uiErrorMessage;
        state.form.isFetching = false;
        state.form.status = status;
      })

      // USER OPTIONS
      .addCase(fetchUserOptions.pending, (state) => {
        state.userOptions.isFetching = true;
      })
      .addCase(fetchUserOptions.fulfilled, (state, { payload }) => {
        const { results_in_datastore, notification_emails, admin_unflagged, response_store, timezone, verified } =
          payload;

        state.userOptions.results_in_datastore = results_in_datastore;
        state.userOptions.timezone = timezone || getClientTimezone();
        state.userOptions.admin_unflagged = admin_unflagged;
        state.userOptions.verified = verified;
        state.userOptions.isFetching = false;

        // Map notification emails
        if (notification_emails && notification_emails.length) {
          state.userOptions.notificationEmails = notification_emails.map((email) => ({ title: email }));
        }

        // Adjust response store
        if (response_store === 'google_sheet' && results_in_datastore) {
          state.userOptions.response_store = 'formplus_sheet';
        } else {
          state.userOptions.response_store = response_store;
        }
      })
      .addCase(fetchUserOptions.rejected, (state, { payload }) => {
        state.userOptions.error = payload.uiErrorMessage;
        state.userOptions.isFetching = false;
      })

      // ALL SPREADSHEETS
      .addCase(fetchAllSheetsTitles.pending, (state) => {
        state.allSheets.isFetching = true;
      })
      .addCase(fetchAllSheetsTitles.fulfilled, (state, { payload }) => {
        state.allSheets.sheets = payload.sheets;
        state.allSheets.isFetching = false;
      })
      .addCase(fetchAllSheetsTitles.rejected, (state, { payload }) => {
        state.allSheets.error = payload.uiErrorMessage;
        state.allSheets.isFetching = false;
      })

      // FORM SPREADSHEETS (used during form transfer)
      .addCase(fetchFormSheetTitles.pending, (state) => {
        state.formSheets.isFetching = true;
      })
      .addCase(fetchFormSheetTitles.fulfilled, (state, { payload }) => {
        const { sheets } = payload;
        state.formSheets.isFetching = false;
        state.formSheets.retrieved = true;

        if (sheets && sheets.length) {
          state.formSheets.selected = [sheets[0]]; // by default we select to copy only the first sheet
          state.formSheets.titles = sheets;
        }
      })
      .addCase(fetchFormSheetTitles.rejected, (state, { payload }) => {
        state.formSheets.error = payload.uiErrorMessage;
        state.formSheets.isFetching = false;
      })

      // USER CREDENTIALS
      .addCase(fetchUserCredentials.pending, (state) => {
        state.userOptions.credentials.isFetching = true;
      })
      .addCase(fetchUserCredentials.fulfilled, (state, { payload }) => {
        const { flutterwave_credentials, paypal_credentials, paystack_credentials, stripe_credentials, error } =
          payload;

        state.userOptions.credentials.isFetching = false;

        const parseCredentials = (credentials, defaultValues) =>
          JSON.parse(credentials) ? JSON.parse(credentials) : defaultValues;

        state.userOptions.credentials.flutterwave = parseCredentials(flutterwave_credentials, {
          public_key: '',
          private_key: '',
        });

        state.userOptions.credentials.paypal = parseCredentials(paypal_credentials, {
          client_id: '',
          test_client_id: '',
        });

        state.userOptions.credentials.paystack = parseCredentials(paystack_credentials, {
          public_key: '',
          test_public_key: '',
        });

        state.userOptions.credentials.stripe = parseCredentials(stripe_credentials, {});
      })
      .addCase(fetchUserCredentials.rejected, (state, { payload }) => {
        state.userOptions.credentials.error = payload.uiErrorMessage;
        state.userOptions.credentials.isFetching = false;
      })

      // ACCOUNT QUOTA
      .addCase(fetchAccountQuota.pending, (state) => {
        state.accountQuota.isFetching = true;
      })
      .addCase(fetchAccountQuota.fulfilled, (state, { payload }) => {
        const { geolocation_quota, emails_used } = payload;

        state.accountQuota.isFetching = false;

        if (geolocation_quota) {
          const { remaining, limit, used } = geolocation_quota;
          state.accountQuota.geolocation = { remaining, limit, used };
        }

        state.accountQuota.emails.used = emails_used;

        const emailsPerMonth = win.PLAN.emails_per_month;
        state.accountQuota.emails.total = emailsPerMonth === Infinity ? 'Unlimited' : Number(emailsPerMonth);
        state.accountQuota.emails.percentage = parseInt((Number(emails_used) / Number(emailsPerMonth)) * 100);
      })
      .addCase(fetchAccountQuota.rejected, (state, { payload }) => {
        state.accountQuota.error = payload.uiErrorMessage;
        state.accountQuota.isFetching = false;
      })

      // FORM CUSTOM TEXTS
      .addCase(fetchFormCustomTexts.pending, (state) => {
        state.formCustomTexts.isFetching = true;
      })
      .addCase(fetchFormCustomTexts.fulfilled, (state, { payload }) => {
        state.formCustomTexts.texts = payload.id ? payload : {};
        state.formCustomTexts.isFetching = false;
      })
      .addCase(fetchFormCustomTexts.rejected, (state, { payload }) => {
        state.formCustomTexts.error = payload.uiErrorMessage;
        state.formCustomTexts.isFetching = false;
      })

      // FORM PAYMENT TRANSACTIONS
      .addCase(fetchFormPaymentHistory.pending, (state) => {
        state.formPaymentHistory.isFetching = true;
        state.formPaymentHistory.error = '';
      })
      .addCase(fetchFormPaymentHistory.fulfilled, (state, { payload }) => {
        const { data, headers, prev_cursor, next_cursor, direction, more, error } = payload;
        state.formPaymentHistory.isRefreshing = false;
        state.formPaymentHistory.isFetching = false;

        state.formPaymentHistory = {
          ...state.formPaymentHistory,
          columns: headers,
          transactions: data,
          prevCursor: prev_cursor,
          nextCursor: next_cursor,
          prevMore: direction === 'back' ? more : prev_cursor !== '',
          nextMore: direction === 'forward' ? more : next_cursor !== '',
        };
      })
      .addCase(fetchFormPaymentHistory.rejected, (state, { payload }) => {
        state.formPaymentHistory.error = payload.uiErrorMessage;
        state.formPaymentHistory.isRefreshing = false;
        state.formPaymentHistory.isFetching = false;
      })

      // CUSTOM DOCUMENT TEMPLATES
      .addCase(fetchCustomDocTemplates.pending, (state) => {
        state.customDocuments.isFetching = true;
        state.customDocuments.error = '';
      })
      .addCase(fetchCustomDocTemplates.fulfilled, (state, { payload }) => {
        const { templates, headers, error } = payload;
        state.customDocuments.isFetching = false;

        for (const template of templates) {
          if (template.template_type === 'notification') {
            state.customDocuments.notifTemplate = template.template_key;
          }
          if (template.template_type === 'autoresponder') {
            state.customDocuments.autorespTemplate = template.template_key;
          }
        }

        state.customDocuments = {
          ...state.customDocuments,
          templates,
          columns: headers,
        };
      })
      .addCase(fetchCustomDocTemplates.rejected, (state, { payload }) => {
        state.customDocuments.error = payload.uiErrorMessage;
        state.customDocuments.isFetching = false;
      })

      // NOTIFICATION EMAIL CONFIG VARIABLES
      .addCase(fetchNotificationEmailConfig.pending, (state) => {
        state.notificationEmailConfig.isFetching = true;
        state.notificationEmailConfig.error = '';
      })
      .addCase(fetchNotificationEmailConfig.fulfilled, (state, { payload }) => {
        state.notificationEmailConfig.isFetching = false;
        state.notificationEmailConfig.values = payload;
      })
      .addCase(fetchNotificationEmailConfig.rejected, (state, { payload }) => {
        state.notificationEmailConfig.error = payload.uiErrorMessage;
        state.notificationEmailConfig.isFetching = false;
      })

      // EXTRA AUTORESPONDERS CONFIG VARIABLES
      .addCase(fetchExtraAutoresponders.pending, (state) => {
        state.extraAutoresponders.isFetching = true;
        state.extraAutoresponders.error = '';
      })
      .addCase(fetchExtraAutoresponders.fulfilled, (state, { payload }) => {
        state.extraAutoresponders.values = payload.extras;
        state.extraAutoresponders.isFetching = false;
      })
      .addCase(fetchExtraAutoresponders.rejected, (state, { payload }) => {
        state.extraAutoresponders.error = payload.uiErrorMessage;
        state.extraAutoresponders.isFetching = false;
      });
  },
});

export const {
  updateCustomDocumentsProcessKeys,
  updateNotificationEmailConfig,
  setIsRefreshingPaymentHistory,
  updateCustomDocumentTemplates,
  setCustomDocumentUploadProps,
  setPendingStorageServiceInfo,
  setTrackedNotificationValues,
  setStorageServicesStatus,
  createExtraAutoresponder,
  updateFormPaymentHistory,
  deleteExtraAutoresponder,
  updateExtraAutoresponder,
  setFormTransferProps,
  setActiveSidebarTab,
  toggleMobileSidebar,
  setUserOptionsProps,
  setTransferringForm,
  setFormCustomTexts,
  setFormSheetsProps,
  setFormJsonEdited,
  setSelectedSheets,
  setSavingOptions,
  setNewOwnerEmail,
  setCopyResults,
  setFormProps,
  setUIStatus,
} = settingsSlice.actions;

export default settingsSlice.reducer;
